import {
    API_ENDPOINTS,
    Button,
    ButtonTypes,
    Card,
    CardBody,
    customToast,
    FileHelpers,
    Icons,
    MultiFileInput,
    Roles,
    route,
    Sizes,
    StringHelpers,
    SubTitle,
    UploadDataType,
    useAuthorisationContext,
    useDisabledContext,
    useLangContext,
    Variants,
} from 'carrier-fe';
import axios from 'axios';
import { useState } from 'react';
import { WarrantyTemplate } from '../../../../types/companies/view';

interface WarrantyTemplatesListProps {
    companyId: string;
    templates: WarrantyTemplate[];
    onChange: () => void;
}

/**
 * WarrantyTemplatesList
 *
 * Per-warranty-level template upload grid for distributor companies. Renders
 * one row per Toshiba warranty level (T5/T7/T10) with the current template
 * (if any) and an upload control. Falls back to the global Toshiba template
 * server-side when a level has no uploaded template.
 */
function WarrantyTemplatesList({ companyId, templates, onChange }: WarrantyTemplatesListProps) {
    const { fields, crud } = useLangContext();
    const { disabled, setDisabled } = useDisabledContext();
    const { hasRoleAccess } = useAuthorisationContext();
    const [pendingFiles, setPendingFiles] = useState<Record<string, any>>({});

    const canManage =
        hasRoleAccess(Roles.SUPER_ADMIN)
        || hasRoleAccess(Roles.PLATFORM_MANAGER)
        || hasRoleAccess(Roles.DISTRIBUTION_MANAGER);

    const getUploadUrl = async (): Promise<UploadDataType | undefined> => {
        try {
            const response = await axios.get(
                route(API_ENDPOINTS.ADMIN.COMPANY.WARRANTY_TEMPLATE_UPLOAD_URL, {
                    companyId,
                })
            );
            return response?.data?.data;
        } catch (err: any) {
            customToast({
                title: err?.response?.data?.message || 'Failed to obtain upload URL',
                variant: Variants.Danger,
            });
            return undefined;
        }
    };

    const buildEndpoint = (warrantyLevelId: string) =>
        route(API_ENDPOINTS.ADMIN.COMPANY.WARRANTY_TEMPLATE, {
            companyId,
            warrantyLevelId,
        });

    const upload = async (warrantyLevelId: string) => {
        const file = pendingFiles[warrantyLevelId];
        if (!file?.base64) {
            customToast({
                title: 'Please choose a file before uploading',
                variant: Variants.Danger,
            });
            return;
        }

        setDisabled(true);
        try {
            const uploadData = await getUploadUrl();
            if (!uploadData) return;

            const uploadResponse = await FileHelpers.upload(file, uploadData);
            if (!uploadResponse?.ok) {
                throw new Error('Upload to S3 failed');
            }

            await axios.post(buildEndpoint(warrantyLevelId), {
                template: uploadData.key,
            });

            customToast({
                title:
                    crud?.responses?.update
                    || 'Template updated successfully',
                variant: Variants.Success,
            });

            setPendingFiles((prev) => {
                const next = { ...prev };
                delete next[warrantyLevelId];
                return next;
            });
            onChange();
        } catch (error: any) {
            customToast({
                title:
                    error?.response?.data?.message
                    || error?.message
                    || 'Failed to update template',
                variant: Variants.Danger,
            });
        } finally {
            setDisabled(false);
        }
    };

    const remove = async (warrantyLevelId: string) => {
        setDisabled(true);
        try {
            await axios.delete(buildEndpoint(warrantyLevelId));

            customToast({
                title:
                    crud?.responses?.delete
                    || 'Template removed',
                variant: Variants.Success,
            });

            onChange();
        } catch (error: any) {
            customToast({
                title:
                    error?.response?.data?.message
                    || 'Failed to remove template',
                variant: Variants.Danger,
            });
        } finally {
            setDisabled(false);
        }
    };

    return (
        <Card>
            <CardBody>
                <SubTitle
                    style={{ color: '#464C5E' }}
                    title={StringHelpers.title(
                        fields?.warranty_templates
                        || 'Warranty certificate templates'
                    )}
                    className="mb-3"
                />
                <p className="text-muted mb-4">
                    {fields?.warranty_templates_description
                        || 'Upload a template PDF for each warranty level. Installer warranty certificates issued under your distribution will use these templates as the base. If no template is uploaded for a level, the default Toshiba template is used.'}
                </p>

                {templates.map((template) => (
                    <Card key={template.toshiba_warranty_level_id} className="mb-3">
                        <CardBody>
                            <div className="d-flex align-items-center justify-content-between mb-3">
                                <div>
                                    <strong>{template.level_name}</strong>
                                    <span className="text-muted ms-2">
                                        ({template.years} {template.years === 1 ? 'year' : 'years'})
                                    </span>
                                </div>
                                {template.has_template && template.template_url && (
                                    <a
                                        href={template.template_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-outline-dark btn-sm"
                                    >
                                        {StringHelpers.title(
                                            crud?.buttons?.view?.default
                                            || 'View current'
                                        )}
                                    </a>
                                )}
                            </div>

                            {canManage && (
                                <>
                                    <MultiFileInput
                                        value={
                                            pendingFiles[template.toshiba_warranty_level_id]
                                                ? [pendingFiles[template.toshiba_warranty_level_id]]
                                                : []
                                        }
                                        maxFiles={1}
                                        acceptedFileTypes={{ 'application/pdf': ['.pdf'] }}
                                        onChange={(value) => {
                                            setPendingFiles((prev) => ({
                                                ...prev,
                                                [template.toshiba_warranty_level_id]: value?.[0] ?? null,
                                            }));
                                        }}
                                        label={StringHelpers.title(
                                            fields?.template_file
                                            || 'Template file (PDF, max 5MB)'
                                        )}
                                        onReject={(value) => {
                                            customToast({
                                                title: value || "Error with the file you've selected",
                                                variant: Variants.Danger,
                                            });
                                        }}
                                    />
                                    <div className="d-flex gap-2 justify-content-end">
                                        {template.has_template && (
                                            <Button
                                                label={StringHelpers.title(
                                                    crud?.buttons?.delete?.default
                                                    || 'Remove template'
                                                )}
                                                onClick={() => remove(template.toshiba_warranty_level_id)}
                                                disabled={disabled}
                                                type={ButtonTypes.Outline}
                                                variant={Variants.Danger}
                                                size={Sizes.Small}
                                                rightIcon={Icons.DELETE}
                                            />
                                        )}
                                        <Button
                                            label={StringHelpers.title(
                                                template.has_template
                                                    ? crud?.buttons?.replace?.default || 'Replace template'
                                                    : crud?.buttons?.upload?.default || 'Upload template'
                                            )}
                                            onClick={() => upload(template.toshiba_warranty_level_id)}
                                            disabled={
                                                disabled
                                                || !pendingFiles[template.toshiba_warranty_level_id]?.base64
                                            }
                                            variant={Variants.Dark}
                                            size={Sizes.Small}
                                            rightIcon={Icons.ADD}
                                        />
                                    </div>
                                </>
                            )}
                        </CardBody>
                    </Card>
                ))}
            </CardBody>
        </Card>
    );
}

export default WarrantyTemplatesList;
